/**
 * One sounded hit, drawn: what the Grid shows when the Walker strikes an
 * Event, and how each Form of Fuckery on it looks. Every hit floods its Cell
 * red and throws a square shock ring out, farther for a kick than for a hat.
 * Each Form draws what it does to the sound, so it can be told at a glance:
 *
 * - Ratchet and Roll strobe the flood and the ring two, four or eight times
 *   inside the Step, tighter rings, a buzz.
 * - Reverse swells: rings close in on the Cell and the flood grows through the
 *   Step, flashing on the attack at its end.
 * - Echo throws ghosts of the hit bouncing left and right, a triplet eighth
 *   apart, each smaller and fainter.
 * - Spring rings round, not square: slow wobbling circles, and drips falling
 *   off the Cell long after.
 * - Crush breaks the hit into a flickering mosaic of Cells, stepped and coarse.
 * - Drive blows the flood up and tears it: a fat jagged ring and scanlines
 *   slipping sideways.
 * - Strings bloom out of the hit with no attack of their own: four taut lines
 *   across the Cell that swell in and shimmer, each at its note's rate, so
 *   the chord beats against itself, then settle and fade. Long before the
 *   sound does: it hums for 12 s, which would clutter the Grid.
 *
 * Drawing only; checked by eye.
 */

import { CELL_PX, WALKER } from "./look.ts"
import { HAT, KICK, SNARE, TOM } from "./world.ts"

/** A plain hit's life: the flood and the shock ring. */
const HIT_MS = 420
/** How far past its Cell a hit's ring runs, in Cells, by what it struck. */
const REACH: Readonly<Record<number, number>> = { [KICK]: 3.2, [SNARE]: 2.4, [TOM]: 1.9, [HAT]: 1.1 }
const SPRING_MS = 1700
const CRUSH_MS = 520
const CRUSH_HOLD_MS = 45
const STRINGS_MS = 3800
/** How long the Strings take to swell in. */
const STRINGS_BLOOM_MS = 450
/** Each string's shimmer, in Hz: a slow 3 Hz times the chord's ratios, E2 B2 D3 G3. */
const STRINGS_HZ = [3, 3 * 1.4983, 3 * 1.7818, 3 * 2.3784] as const
/** Where each string crosses the Cell, in Cells from its centre. */
const STRINGS_AT = [-0.33, -0.11, 0.11, 0.33] as const
/** How far each string runs either side of the Cell's centre, in Cells. */
const STRINGS_HALF = 1.7

export interface Strike {
  /** The page time after which it draws nothing. */
  readonly end: number
  /**
   * Draw it at the Cell centred on x, y. Returns how strongly the Cell is
   * flooded now, 0 to 1, for the glyph to be knocked out of it.
   */
  readonly draw: (context: CanvasRenderingContext2D, x: number, y: number, now: number) => number
}

const easeOut = (t: number): number => 1 - (1 - t) ** 3

/** A repeatable 0-to-1 from two integers: the same mosaic and drips on every frame, and for every Visitor. */
const hash = (a: number, b: number): number => {
  let h = Math.imul(a, 374761393) + Math.imul(b, 668265263)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

/** A square of half-side `half`, its edges torn into teeth. */
const jagged = (context: CanvasRenderingContext2D, x: number, y: number, half: number, tooth: number) => {
  const corners = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1]
  ] as const
  const teeth = Math.max(2, Math.round((half * 2) / (tooth * 2)))
  context.moveTo(x - half, y - half)
  for (let side = 0; side < 4; side++) {
    const [ax, ay] = corners[side]!
    const [bx, by] = corners[(side + 1) % 4]!
    // The outward normal of this side.
    const nx = (ax + bx) / 2
    const ny = (ay + by) / 2
    for (let i = 1; i <= teeth * 2; i++) {
      const f = i / (teeth * 2)
      const out = i % 2 === 1 ? tooth : 0
      context.lineTo(x + half * (ax + (bx - ax) * f) + nx * out, y + half * (ay + (by - ay) * f) + ny * out)
    }
  }
  context.closePath()
}

export const strikeOf = (
  value: number,
  forms: ReadonlyArray<string> | undefined,
  stepMs: number,
  at: number,
  cell: number
): Strike => {
  const has = (form: string) => forms?.includes(form) === true
  const ratchet = has("ratchet")
  const roll = has("roll")
  const reverse = has("reverse")
  const echo = has("echo")
  const spring = has("spring")
  const crush = has("crush")
  const drive = has("drive")
  const strings = has("strings")

  const repeats = (ratchet ? 2 : 1) * (roll ? 4 : 1)
  const sub = stepMs / repeats
  const reach = (REACH[value] ?? 1) * CELL_PX * (repeats > 1 ? 0.75 : 1) * (drive ? 1.3 : 1)
  // An Echo's repeats: an eighth-note triplet apart, two thirds of two Steps.
  const echoGap = (stepMs * 4) / 3
  // A Reverse's attack lands at the end of its Step, and everything after it waits for it.
  const attack = reverse ? stepMs : 0

  const end =
    at +
    Math.max(
      attack + HIT_MS,
      echo ? echoGap * 3 + HIT_MS : 0,
      spring ? SPRING_MS + 900 : 0,
      crush ? CRUSH_MS : 0,
      strings ? attack + STRINGS_MS : 0
    )

  /** How flooded the Cell is `age` ms in: a decay per repeat, or a swell into the attack. */
  const floodAt = (age: number): { readonly level: number; readonly local: number } => {
    if (reverse && age < attack) return { level: (age / attack) ** 3, local: 1 }
    const since = age - attack
    const life = repeats > 1 ? sub * 0.85 : HIT_MS
    let level = 0
    let local = 1
    for (let i = 0; i < repeats; i++) {
      const t = (since - (reverse ? 0 : i * sub)) / life
      if (t < 0 || t >= 1) continue
      const l = (1 - t) ** 2
      if (l > level) {
        level = l
        local = t
      }
      if (reverse) break
    }
    return { level, local }
  }

  const quantise = (alpha: number) => (crush ? Math.ceil(alpha * 3) / 3 : alpha)

  const drawRing = (
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    half: number,
    weight: number
  ) => {
    context.lineWidth = weight * (drive ? 2.6 : 1)
    context.beginPath()
    if (crush) {
      // Coarse: the ring's edge snaps to a 4 px lattice, and breaks into bits.
      const snapped = Math.round(half / 4) * 4
      context.setLineDash([3, 4])
      context.rect(x - snapped, y - snapped, snapped * 2, snapped * 2)
    } else if (drive) jagged(context, x, y, half, 3)
    else context.rect(x - half, y - half, half * 2, half * 2)
    context.stroke()
    context.setLineDash([])
  }

  const draw: Strike["draw"] = (context, x, y, now) => {
    const age = now - at
    context.fillStyle = WALKER
    context.strokeStyle = WALKER
    context.lineCap = "butt"

    // The flood: the whole Cell, bigger for an instant as it strikes.
    const flood = floodAt(age)
    if (flood.level > 0) {
      const punch = 1 + (drive ? 1 : 0.45) * Math.max(0, 1 - flood.local * 4)
      const size = CELL_PX * (reverse && age < attack ? 0.4 + 0.6 * flood.level : punch)
      context.globalAlpha = quantise(flood.level)
      context.fillRect(x - size / 2, y - size / 2, size, size)
    }

    // The shock rings: out from the Cell per repeat, or closing in on it through a Reverse.
    if (reverse) {
      const count = Math.max(3, repeats)
      for (let i = 0; i < count; i++) {
        const start = (attack * i) / (count + 1)
        const r = (age - start) / (attack - start)
        if (r < 0 || r >= 1) continue
        context.globalAlpha = quantise(0.25 + 0.75 * r)
        drawRing(context, x, y, CELL_PX / 2 + (1 - easeOut(r)) * reach * 1.2, 1 + 2 * r)
      }
    }
    const ringLife = repeats > 1 ? Math.max(sub * 2.2, 180) : HIT_MS
    for (let i = 0; i < repeats; i++) {
      const r = (age - attack - (reverse ? 0 : i * sub)) / ringLife
      if (r < 0 || r >= 1) continue
      context.globalAlpha = quantise((1 - r) * (1 - i / (repeats + 1)))
      drawRing(context, x, y, CELL_PX / 2 + easeOut(r) * reach, 0.75 + 2.5 * (1 - r))
      if (reverse) break
    }

    // Echo: ghosts bouncing left and right, one Cell farther each time, smaller and fainter.
    if (echo) {
      for (let i = 1; i <= 3; i++) {
        const t = (age - attack - echoGap * i) / (HIT_MS * 0.8)
        if (t < 0 || t >= 1) continue
        const gx = x + (i % 2 === 1 ? -i : i) * CELL_PX
        const strength = 0.75 * 0.62 ** (i - 1) * (1 - t) ** 2
        const size = CELL_PX * (1 - 0.14 * i)
        context.globalAlpha = quantise(strength)
        context.fillRect(gx - size / 2, y - size / 2, size, size)
        context.globalAlpha = quantise(strength * 0.8)
        drawRing(context, gx, y, size / 2 + easeOut(t) * reach * 0.5, 1.5)
      }
    }

    // Spring: round, slow, wobbling ripples, and drips falling off the Cell long after.
    if (spring) {
      context.lineWidth = 1.5
      for (let i = 0; i < 4; i++) {
        const r = (age - i * 130) / SPRING_MS
        if (r < 0 || r >= 1) continue
        const wobble = Math.sin(age / 21 + i * 1.7) * 3 * (1 - r)
        context.globalAlpha = quantise(0.85 * (1 - r) ** 1.5)
        context.beginPath()
        context.arc(x, y + wobble * 0.3, CELL_PX * 0.6 + easeOut(r) * reach * 1.7 + wobble, 0, Math.PI * 2)
        context.stroke()
      }
      for (let i = 0; i < 7; i++) {
        const t = (age - 180 - i * 190 - hash(cell, i) * 120) / 900
        if (t < 0 || t >= 1) continue
        const dx = (hash(cell, i + 50) - 0.5) * CELL_PX * 3
        const fall = CELL_PX * (0.4 + 5 * t * t)
        context.globalAlpha = quantise(0.9 * (1 - t))
        context.beginPath()
        context.ellipse(x + dx, y + fall, 1.8, 1.8 + 3 * t, 0, 0, Math.PI * 2)
        context.fill()
      }
    }

    // Strings: four lines swelling in across the Cell, each shimmering as a
    // plucked string does, still at its ends and widest in its middle.
    if (strings) {
      const since = age - attack
      const t = since / STRINGS_MS
      if (t >= 0 && t < 1) {
        const bloom = Math.min(1, since / STRINGS_BLOOM_MS)
        const swell = bloom * bloom * (3 - 2 * bloom)
        const level = swell * (1 - t) ** 1.5
        const half = CELL_PX * STRINGS_HALF * (0.35 + 0.65 * easeOut(bloom))
        const segments = 24
        context.lineWidth = 1
        for (let k = 0; k < STRINGS_AT.length; k++) {
          const sy = y + STRINGS_AT[k]! * CELL_PX
          const phase = hash(cell, k + 70) * Math.PI * 2
          const sway = Math.sin((since / 1000) * Math.PI * 2 * STRINGS_HZ[k]! + phase)
          const amplitude = CELL_PX * 0.16 * level * sway
          context.globalAlpha = quantise(0.85 * level)
          context.beginPath()
          for (let i = 0; i <= segments; i++) {
            const u = i / segments
            const px = x - half + u * half * 2
            const py = sy + amplitude * Math.sin(Math.PI * u)
            if (i === 0) context.moveTo(px, py)
            else context.lineTo(px, py)
          }
          context.stroke()
        }
      }
    }

    // Crush: a mosaic of Cells round the hit, flickering in held frames, coarse steps of red.
    if (crush) {
      const t = (age - attack) / CRUSH_MS
      if (t >= 0 && t < 1) {
        const frame = Math.floor((age - attack) / CRUSH_HOLD_MS)
        const radius = Math.ceil(reach / CELL_PX)
        const spread = 1 + t * radius
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) > spread) continue
            const roll = hash(cell * 97 + (dx + 8) * 17 + (dy + 8), frame)
            if (roll > 0.42 * (1 - t)) continue
            context.globalAlpha = roll < 0.12 ? 1 : roll < 0.26 ? 2 / 3 : 1 / 3
            context.fillRect(
              x + dx * CELL_PX - CELL_PX / 2 + 2,
              y + dy * CELL_PX - CELL_PX / 2 + 2,
              CELL_PX - 4,
              CELL_PX - 4
            )
          }
        }
      }
    }

    // Drive: scanlines torn sideways across the hit, for the first moment of it.
    if (drive) {
      const t = (age - attack) / 160
      if (t >= 0 && t < 1) {
        const frame = Math.floor((age - attack) / 40)
        for (let i = 0; i < 4; i++) {
          const ly = y + (hash(cell + frame, i) - 0.5) * reach * 2
          const shift = (hash(cell + frame, i + 9) - 0.5) * CELL_PX * 2
          const width = reach * (1 + hash(cell + frame, i + 20))
          context.globalAlpha = 1 - t
          context.fillRect(x - width / 2 + shift, ly, width, 2 + 3 * hash(cell, i + frame))
        }
      }
    }

    context.globalAlpha = 1
    return flood.level
  }

  return { end, draw }
}
